/**
 * Canonical parse/format helpers for the `URL` additional-field type.
 *
 * A URL field value is stored as a single scalar string in one of two shapes:
 *   - bare URL:          https://example.com
 *   - markdown link:     [Display text](https://example.com)
 *
 * Keeping it a scalar string (not an object) means import, export, filter and
 * the guest `additionalFields` Map all keep working unchanged. The `{url,label}`
 * object only exists transiently inside the renderer.
 *
 * IMPORTANT: this logic is mirrored byte-for-byte in
 *   - website-builder-backend/src/lib/additional-field-validator.ts
 *   - main-backend .../additional_field/AdditionalFieldConstraints.java (regex)
 * There is no CI sync test — change all copies together.
 *
 * Limitation: the display text may not contain `]`, the URL may not contain
 * `)` or whitespace (would break the markdown syntax). The two-field editor
 * rejects such input.
 */

/**
 * Core URL grammar (mirrored in every validator — see header).
 * http(s) scheme + a host with at least one dot and a 2+ letter TLD (so
 * `https://sadf` and `https://localhost` are rejected) + optional port +
 * optional path/query/fragment. No whitespace, no `)` (would break the
 * markdown `[label](url)` wrapper).
 */
const URL_CORE =
  "https?:\\/\\/(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\\.)+[a-zA-Z]{2,}(?::\\d+)?(?:[/?#][^\\s)]*)?";

/** Bare URL — a full `URL_CORE` and nothing else. */
export const URL_FIELD_BARE_PATTERN = new RegExp(`^${URL_CORE}$`, "i");

/** Markdown link `[label](url)` — label in group 1, url in group 2. */
export const URL_FIELD_MD_PATTERN = new RegExp(
  `^\\[([^\\]]*)\\]\\((${URL_CORE})\\)$`,
  "i",
);

/** Hard cap, mirrors backend `STRING_LENGTH_GLOBAL_MAX`. */
export const URL_FIELD_MAX_LENGTH = 2000;

/** Only `http(s)` is a safe scheme — blocks `javascript:` / `data:` etc. */
export function isSafeHttpUrl(url: string): boolean {
  return /^https?:\/\//i.test(url.trim()) && !/\s/.test(url.trim());
}

/**
 * Split a stored URL-field value into its `{url, label}` parts for the
 * editor. Lenient on purpose — it round-trips a work-in-progress or
 * not-yet-valid value so the editor never blanks out what the user typed.
 * Strict validity is a separate concern ({@link isValidUrlFieldValue}).
 */
export function parseUrlField(raw: unknown): { url: string; label: string } {
  if (typeof raw !== "string") return { url: "", label: "" };
  const trimmed = raw.trim();
  if (!trimmed) return { url: "", label: "" };

  // Loose markdown split — extract label + url even when the url is not
  // (yet) a valid http(s) URL.
  const md = /^\[([^\]]*)\]\((.*)\)$/.exec(trimmed);
  if (md) return { label: md[1] ?? "", url: md[2] ?? "" };

  return { url: trimmed, label: "" };
}

/**
 * Graceful input fix for a raw URL the user typed: trim, and when the value
 * carries no URI scheme at all, prepend `https://` (so `veltric.com` →
 * `https://veltric.com`). A value that already has a scheme — good or bad —
 * is left untouched so it surfaces in save-time validation rather than being
 * silently rewritten. Empty stays empty.
 */
export function normalizeUrlInput(raw: string): string {
  const t = raw.trim();
  if (!t) return "";
  if (/^https?:\/\//i.test(t)) return t; // already http(s)
  if (t.includes("://")) return t; // some other scheme — leave for validation
  return `https://${t}`;
}

/**
 * Compose a canonical stored value from the two editor fields.
 * Empty URL → empty string. No label → bare URL. Label → markdown link.
 */
export function formatUrlField(url: string, label: string): string {
  const u = url.trim();
  const l = label.trim();
  if (!u) return "";
  return l ? `[${l}](${u})` : u;
}

/**
 * Validate a candidate stored value. An empty value is allowed here — the
 * `required` rule is enforced separately by the caller.
 */
export function isValidUrlFieldValue(raw: unknown): boolean {
  if (raw == null || raw === "") return true;
  if (typeof raw !== "string") return false;
  const trimmed = raw.trim();
  if (!trimmed) return true;
  if (trimmed.length > URL_FIELD_MAX_LENGTH) return false;

  const md = URL_FIELD_MD_PATTERN.exec(trimmed);
  if (md) return isSafeHttpUrl(md[2]);

  return URL_FIELD_BARE_PATTERN.test(trimmed) && isSafeHttpUrl(trimmed);
}
